// The on-board nudge (Issue #592, mockup Scenes 1–2). Shadow-mode: the assistant
// suggests a job that fits an open route; the dispatcher decides. "Suggest a fit"
// runs the existing dry-run (apply:false) and surfaces the top proposal. Accept
// places that one job via the existing single-job apply path (a human-initiated
// action, not autonomous) and records the nudge accepted; "Not now" captures a
// one-tap reason chip. Every yes/no teaches the system (POST /api/dispatch/nudges
// + /resolve). Self-contained so the board shell only mounts it.

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Sparkles, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { AutoScheduleResponse } from "./auto-schedule-dialog";

const REASON_CHIPS: { key: string; label: string }[] = [
    { key: "time_window", label: "Customer time window" },
    { key: "access_hours", label: "Access hours" },
    { key: "physical_split", label: "Physical / split the day" },
    { key: "parts", label: "Parts not in" },
    { key: "reliability", label: "Reliability" },
    { key: "route", label: "Route — I've got it" },
];

interface Suggestion {
    jobId: string;
    jobTitle: string;
    siteName: string | null;
    techId: string;
    techName: string;
    date: string;
    reason: string;
}

function firstName(name: string): string {
    return name.split(" ")[0] || name;
}

export function NudgeCard({ date }: { date: string }) {
    const qc = useQueryClient();
    const [suggestion, setSuggestion] = useState<Suggestion | null>(null);
    const [nudgeId, setNudgeId] = useState<string | null>(null);
    const [asking, setAsking] = useState(false); // showing the dismiss reason chips

    function reset() {
        setSuggestion(null);
        setNudgeId(null);
        setAsking(false);
    }

    // Suggestion source = the existing dry-run (apply:false). We take the top
    // proposal's first stop as the nudge, then record it as "offered".
    const findFit = useMutation({
        mutationFn: () =>
            api.post<AutoScheduleResponse>("/api/dispatch/auto-schedule", { fromDate: date, apply: false }),
        onSuccess: async (res) => {
            const proposal = res.proposals?.[0];
            const stop = proposal?.stops?.[0];
            if (!proposal || !stop) {
                toast.info("No fitting jobs to suggest right now.");
                reset();
                return;
            }
            const sug: Suggestion = {
                jobId: stop.jobId,
                jobTitle: stop.jobTitle,
                siteName: stop.siteName ?? null,
                techId: proposal.techId,
                techName: proposal.techName,
                date: proposal.date,
                reason: stop.reason,
            };
            setSuggestion(sug);
            setAsking(false);
            const rec = await api
                .post<{ id: string | null }>("/api/dispatch/nudges", {
                    jobId: sug.jobId,
                    suggestedUserId: sug.techId,
                    suggestedDate: sug.date,
                    rationale: { reason: sug.reason, techName: sug.techName, siteName: sug.siteName },
                })
                .catch(() => null);
            setNudgeId(rec?.id ?? null);
        },
        onError: () => toast.error("Couldn't compute a suggestion."),
    });

    // Accept = place this one job via the existing single-job apply path (the same
    // one the board already exposes) + record the nudge accepted. Human-initiated.
    const accept = useMutation({
        mutationFn: async () => {
            if (!suggestion) return;
            await api.post("/api/dispatch/auto-schedule", { jobIds: [suggestion.jobId], apply: true });
            if (nudgeId) {
                await api
                    .post(`/api/dispatch/nudges/${nudgeId}/resolve`, { outcome: "accepted" })
                    .catch(() => undefined);
            }
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["dispatch"] });
            toast.success(suggestion ? `Added to ${firstName(suggestion.techName)}'s day` : "Added");
            reset();
        },
        onError: () => toast.error("Couldn't add that stop."),
    });

    const dismiss = useMutation({
        mutationFn: (reason: string) =>
            nudgeId
                ? api.post(`/api/dispatch/nudges/${nudgeId}/resolve`, { outcome: "dismissed", dismissReason: reason })
                : Promise.resolve(undefined),
        onSuccess: () => {
            toast.success("Noted — thanks");
            reset();
        },
        onError: () => reset(),
    });

    // Idle prompt — one slim row, on-demand so the board never auto-runs the
    // (expensive) scheduler.
    if (!suggestion) {
        return (
            <Card className="bg-card">
                <CardContent className="flex items-center justify-between gap-3 py-3">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Sparkles className="h-4 w-4 text-primary" />
                        <span>Assistant — find a job that fits an open route?</span>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* #828 M1: the report opens ON THE BOARD'S DATE — it
                            used to land on its own default (yesterday-ish),
                            two days off the board it was opened from. */}
                        <Link
                            to={`/admin/scheduling-report?date=${date}`}
                            className="text-xs font-medium text-primary hover:underline"
                            title="Why the auto-scheduler placed what it did, for this board's date"
                        >
                            Daily report
                        </Link>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => findFit.mutate()}
                            disabled={findFit.isPending}
                        >
                            {findFit.isPending ? (
                                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                            ) : (
                                <Sparkles className="mr-1 h-4 w-4" />
                            )}
                            Suggest a fit
                        </Button>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="border-primary/40 bg-primary/5 backdrop-blur-sm">
            <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 shrink-0 text-primary" />
                    <CardTitle className="text-sm font-semibold">Fits {suggestion.techName}&apos;s route</CardTitle>
                    <Badge variant="secondary" className="ml-auto text-xs">
                        Shadow — you decide
                    </Badge>
                </div>
            </CardHeader>
            <CardContent className="space-y-3 pt-0">
                <div className="text-sm">
                    <span className="font-medium">{suggestion.jobTitle}</span>
                    {suggestion.siteName ? (
                        <span className="text-muted-foreground"> — {suggestion.siteName}</span>
                    ) : null}
                </div>
                {suggestion.reason ? <p className="text-xs text-muted-foreground">{suggestion.reason}</p> : null}

                {!asking ? (
                    <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="success" onClick={() => accept.mutate()} disabled={accept.isPending}>
                            {accept.isPending ? (
                                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                            ) : (
                                <Check className="mr-1 h-4 w-4" />
                            )}
                            Add to {firstName(suggestion.techName)}&apos;s day
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setAsking(true)} disabled={accept.isPending}>
                            Not now
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-2">
                        <p className="text-xs font-medium">Quick — why? (optional)</p>
                        <div className="flex flex-wrap gap-1.5">
                            {REASON_CHIPS.map((c) => (
                                <Button
                                    key={c.key}
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-xs"
                                    onClick={() => dismiss.mutate(c.key)}
                                    disabled={dismiss.isPending}
                                >
                                    {c.label}
                                </Button>
                            ))}
                            <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-xs"
                                onClick={() => dismiss.mutate("other")}
                                disabled={dismiss.isPending}
                            >
                                skip
                            </Button>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
